import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Divider, Stack, type StackProps } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import { useDebouncedCallback } from 'use-debounce'
import {
  type Question,
  type QuestionMode,
  QuestionView,
  QuestionViewNew,
  type QuestionViewProps,
} from '@pbt/pbt-ui-components'
import QuestionContent from '@pbt/pbt-ui-components/src/components/questions/QuestionContent'

import EmptyListPlaceholder from '~/components/common/list-with-filters/EmptyListPlaceholder'
import FeatureToggle from '~/constants/featureToggle'
import {
  getAnswerInputTypes,
  getFeatureToggle,
} from '~/store/reducers/constants'
import { getQuestionsIsLoading } from '~/store/reducers/questions'

const useStyles = makeStyles(
  () => ({
    placeholder: {
      fontSize: '1.6rem',
      alignSelf: 'flex-start',
    },
  }),
  { name: 'QuestionListView' },
)

export interface QuestionListViewProps
  extends Pick<Partial<QuestionViewProps>, 'classes'>,
    Pick<QuestionViewProps, 'allAnswersExpanded' | 'renderPriorAnswers'> {
  StackProps?: StackProps
  className?: string
  editDisabled?: boolean
  mode?: QuestionMode
  onSaveQuestionLog: (log: Question[]) => void
  questionsLog: Question[]
  showNoQuestionsMessage?: boolean
  type: string
  useDivider?: boolean
}

const QuestionListView = ({
  StackProps: StackComponentProps,
  allAnswersExpanded,
  classes: classesProp,
  className: classNameProp,
  editDisabled,
  mode,
  onSaveQuestionLog,
  questionsLog: questionsLogProp,
  renderPriorAnswers,
  showNoQuestionsMessage = false,
  type,
  useDivider = false,
}: QuestionListViewProps) => {
  const classes = useStyles()
  const isCvcIntakeQuestionnaireEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_INTAKE_QUESTIONNAIRE),
  )
  const isFollowQuestionsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOLLOW_UP_QUESTIONS),
  )
  const isQuestionsDynamicTextAndSubtextEnabled = useSelector(
    getFeatureToggle(FeatureToggle.QUESTIONS_DYNAMIC_TEXT_AND_SUBTEXT),
  )
  const isLoading = useSelector(getQuestionsIsLoading)
  const AnswerInputTypes = useSelector(getAnswerInputTypes)
  const { t } = useTranslation('Common')

  const [pendingQuestionId, setPendingQuestionId] = useState<string>()
  const [questionsLog, setQuestionsLog] = useState<Question[]>(questionsLogProp)

  const debouncedSave = useDebouncedCallback(onSaveQuestionLog, 100)

  useEffect(() => {
    setQuestionsLog(questionsLogProp)
  }, [questionsLogProp])

  useEffect(() => {
    if (pendingQuestionId && !isLoading) {
      setPendingQuestionId(undefined)
    }
  }, [isLoading])

  const onQuestionAnswered = (newQuestion: Question) => {
    const oldQuestionIndex = questionsLog.findIndex(
      ({ id }) => id === newQuestion.id,
    )
    const newQuestionsLog = R.update(
      oldQuestionIndex,
      newQuestion,
      questionsLog,
    )

    setPendingQuestionId(newQuestion.id)
    setQuestionsLog(newQuestionsLog)
    debouncedSave(newQuestionsLog)
  }

  const filteredLog = questionsLog.filter(
    (question) => question.workflowIds.indexOf(type) > -1,
  )

  return filteredLog.length > 0 || !showNoQuestionsMessage ? (
    <Stack
      divider={useDivider ? <Divider flexItem /> : null}
      spacing={4}
      {...StackComponentProps}
    >
      {filteredLog.map((question) => {
        const priorAnswers =
          R.is(Boolean, renderPriorAnswers) && R.is(Boolean, allAnswersExpanded)
            ? {
                allAnswersExpanded,
                renderPriorAnswers:
                  renderPriorAnswers &&
                  (question.priorAnswers || [])?.length > 0,
              }
            : {
                allAnswersExpanded: undefined,
                renderPriorAnswers: undefined,
              }

        return isCvcIntakeQuestionnaireEnabled ? (
          <Stack key={question.id} px={2} py={1}>
            <QuestionViewNew
              AnswerInputTypes={AnswerInputTypes}
              disabled={editDisabled}
              isLoading={question.id === pendingQuestionId}
              mode={mode}
              question={{
                ...question,
                questionSubText: isQuestionsDynamicTextAndSubtextEnabled
                  ? question.questionSubText
                  : undefined,
              }}
              renderQuestionContent={(props: any) => (
                <QuestionContent
                  {...props}
                  questionTextStyles={{
                    fontSize: '1.4rem',
                    fontWeight: 500,
                  }}
                />
              )}
              showFollowUpQuestion={isFollowQuestionsEnabled}
              {...priorAnswers}
              onAnswered={onQuestionAnswered}
            />
          </Stack>
        ) : (
          <QuestionView
            AnswerInputTypes={AnswerInputTypes}
            className={classNameProp}
            classes={classesProp}
            disabled={editDisabled}
            isLoading={question.id === pendingQuestionId}
            key={question.id}
            mode={mode}
            question={{
              ...question,
              questionSubText: isQuestionsDynamicTextAndSubtextEnabled
                ? question.questionSubText
                : undefined,
            }}
            {...priorAnswers}
            onAnswered={onQuestionAnswered}
          />
        )
      })}
    </Stack>
  ) : (
    <EmptyListPlaceholder
      categoryName={t('Common:QUESTION_OTHER').toLowerCase()}
      categoryType="questions"
      className={classes.placeholder}
    />
  )
}

export default QuestionListView
